import { motion, useReducedMotion } from "framer-motion";

import Icon from "../../components/Icon";
import type { HistoryEntry } from "../../api/types";

export interface HistoryRowProps {
  entry: HistoryEntry;
  /** Index in the list, used to stagger the enter animation. */
  index?: number;
  onSelect?: (id: number) => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelection?: (entry: HistoryEntry) => void;
}

/**
 * Single history row. Mirrors `.entry-row` markup in main.html so the
 * existing CSS keeps the same look.
 *
 * Wrapped in `motion.li` so newly-added rows fade + slide in. The animation
 * is short (160 ms) and respects `prefers-reduced-motion`. The `<a href="#">`
 * is preserved from the legacy template — clicking does nothing yet.
 */
export default function HistoryRow({
  entry,
  index = 0,
  onSelect,
  selectionMode = false,
  selected = false,
  onToggleSelection,
}: HistoryRowProps) {
  const reduce = useReducedMotion();
  const initial = reduce ? false : { opacity: 0, y: 8 };
  const animate = reduce ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 };
  const rowLabel =
    entry.entry_type === "fuel" ? entry.station_name : "Odometer entry";
  const content =
    entry.entry_type === "fuel" ? (
      <>
        <div className="entry-row__main">
          <div className="entry-row__title">{entry.station_name}</div>
          <div className="entry-row__meta">
            <Icon name="pin" className="entry-row__meta-icon" />
            <span>
              {entry.city}, {entry.country}
            </span>
            <span className="entry-row__dot">·</span>
            <Icon name="calendar" className="entry-row__meta-icon" />
            <span>
              {entry.date} {entry.time}
            </span>
          </div>
        </div>
        <div className="entry-row__side">
          <div className="entry-row__amount">
            {entry.amount_eur.toFixed(2)}
            <span>€</span>
          </div>
          <div className="entry-row__liters">{entry.liters.toFixed(1)} L</div>
        </div>
      </>
    ) : (
      <>
        <div className="entry-row__main">
          <div className="entry-row__title">Odometer entry</div>
          <div className="entry-row__meta">
            <Icon name="calendar" className="entry-row__meta-icon" />
            <span>
              {entry.date} {entry.time}
            </span>
          </div>
        </div>
        <div className="entry-row__side">
          <div className="entry-row__amount">
            {entry.odometer_km.toLocaleString(undefined, {
              maximumFractionDigits: Number.isInteger(entry.odometer_km) ? 0 : 1,
            })}
            <span>km</span>
          </div>
        </div>
      </>
    );

  return (
    <motion.li
      className="history-list__item"
      initial={initial}
      animate={animate}
      transition={{
        duration: 0.16,
        ease: "easeOut",
        // Cap the stagger so a long list doesn't feel sluggish.
        delay: Math.min(index, 8) * 0.03,
      }}
    >
      {selectionMode ? (
        <label
          className={"entry-row entry-row--selectable" + (selected ? " entry-row--selected" : "")}
          data-entry-id={`${entry.entry_type}:${entry.id}`}
        >
          <span className="entry-row__select">
            <input
              type="checkbox"
              checked={selected}
              aria-label={`Select ${rowLabel}`}
              onChange={() => onToggleSelection?.(entry)}
            />
          </span>
          {content}
        </label>
      ) : entry.entry_type === "fuel" ? (
        <a
          href="#"
          className="entry-row"
          data-entry-id={`fuel:${entry.id}`}
          onClick={(event) => {
            event.preventDefault();
            onSelect?.(entry.id);
          }}
        >
          {content}
        </a>
      ) : (
        <div className="entry-row entry-row--static" data-entry-id={`odometer:${entry.id}`}>
          {content}
        </div>
      )}
    </motion.li>
  );
}
